import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../config';
import './AdminDisponibilitesPage.css';

function formatDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function AdminDisponibilitesPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const [currentDate, setCurrentDate] = useState(new Date());
  const [creneauxParJour, setCreneauxParJour] = useState({}); // { "2026-09-05": [{heure, disponible}, ...] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedDay, setSelectedDay] = useState(null); // "2026-09-05" ou null
  const [editingHeures, setEditingHeures] = useState([]); // [{heure: "09:00", disponible: true}, ...]
  const [newHeure, setNewHeure] = useState('');
  const [saving, setSaving] = useState(false);
  const [dupliquer, setDupliquer] = useState(false);
  const [nbSemaines, setNbSemaines] = useState(4);

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token
  };

  const loadMonth = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const debut = formatDateLocal(new Date(year, month, 1));
    const fin = formatDateLocal(new Date(year, month + 1, 0));

    setLoading(true);
    fetch(`${API_URL}/creneaux?debut=${debut}&fin=${fin}`)
      .then(res => res.json())
      .then(data => {
        const grouped = {};
        data.forEach(c => {
          if (!grouped[c.jour]) grouped[c.jour] = [];
          grouped[c.jour].push({ heure: c.heure, disponible: c.disponible });
        });
        Object.values(grouped).forEach(list => list.sort((a, b) => a.heure.localeCompare(b.heure)));
        setCreneauxParJour(grouped);
        setLoading(false);
      })
      .catch(() => {
        setError("Impossible de charger les disponibilités");
        setLoading(false);
      });
  }, [currentDate]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadMonth();
  }, [token, navigate, loadMonth]);

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const previousMonth = () => {
    setSelectedDay(null);
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setSelectedDay(null);
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const openDay = (dateStr) => {
    setSelectedDay(dateStr);
    setEditingHeures(creneauxParJour[dateStr] ? [...creneauxParJour[dateStr]] : []);
    setNewHeure('');
    setDupliquer(false);
  };

  const closeDay = () => {
    setSelectedDay(null);
    setEditingHeures([]);
  };

  const addHeure = () => {
    if (!newHeure) return;
    if (editingHeures.some(h => h.heure === newHeure)) {
      setNewHeure('');
      return;
    }
    setEditingHeures([...editingHeures, { heure: newHeure, disponible: true }].sort((a, b) => a.heure.localeCompare(b.heure)));
    setNewHeure('');
  };

  const removeHeure = (heure) => {
    setEditingHeures(editingHeures.filter(h => h.heure !== heure));
  };

  const putJour = (jourStr, heures) => {
    return fetch(`${API_URL}/creneaux/jour`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ jour: jourStr, heures })
    }).then(res => {
      if (!res.ok) throw new Error();
      return res.json();
    });
  };

  const saveDay = () => {
    setSaving(true);
    const heures = editingHeures.map(h => h.heure);
    const tousLesConflits = [];

    putJour(selectedDay, heures)
      .then(async (data) => {
        if (data.conflits && data.conflits.length > 0) {
          tousLesConflits.push(`${selectedDay} : ${data.conflits.join(', ')}`);
        }

        if (dupliquer && nbSemaines > 0) {
          const base = new Date(selectedDay + 'T00:00:00');
          for (let i = 1; i <= nbSemaines; i++) {
            const future = new Date(base);
            future.setDate(future.getDate() + 7 * i);
            const futureStr = formatDateLocal(future);
            try {
              const resultat = await putJour(futureStr, heures);
              if (resultat.conflits && resultat.conflits.length > 0) {
                tousLesConflits.push(`${futureStr} : ${resultat.conflits.join(', ')}`);
              }
            } catch (e) {
              tousLesConflits.push(`${futureStr} : erreur lors de l'enregistrement`);
            }
          }
        }

        setSaving(false);
        if (tousLesConflits.length > 0) {
          window.alert(
            "Certains horaires étaient déjà réservés et n'ont pas pu être retirés :\n" +
            tousLesConflits.join('\n')
          );
        }
        loadMonth();
        closeDay();
      })
      .catch(() => {
        setSaving(false);
        setError("Erreur lors de l'enregistrement");
      });
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);

    const cells = [];
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="dispo-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDateLocal(date);
      const creneaux = creneauxParJour[dateStr] || [];
      const total = creneaux.length;
      const dispo = creneaux.filter(c => c.disponible).length;
      const isToday = date.toDateString() === new Date().toDateString();

      let badgeClass = 'badge-none';
      if (total > 0 && dispo > 0) badgeClass = 'badge-open';
      if (total > 0 && dispo === 0) badgeClass = 'badge-full';

      cells.push(
        <div
          key={`day-${day}`}
          className={`dispo-day ${isToday ? 'today' : ''} ${selectedDay === dateStr ? 'selected' : ''}`}
          onClick={() => openDay(dateStr)}
        >
          <span className="day-number">{day}</span>
          {total > 0 && <span className={`slot-badge ${badgeClass}`}>{dispo}/{total}</span>}
        </div>
      );
    }

    return cells;
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  if (loading) return <div className="dispo-page"><p>Chargement...</p></div>;

  return (
    <div className="dispo-page">
      <div className="dispo-header">
        <h1>Disponibilités</h1>
        <div className="dispo-header-links">
          <Link to="/admin/vitrine" className="back-link">Vitrine (Collections)</Link>
          <Link to="/admin/gestion" className="back-link">Prestations & capsules</Link>
          <Link to="/admin" className="back-link">Calendrier des réservations</Link>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <p className="dispo-hint">
        Clique sur un jour pour choisir les horaires que tu proposes ce jour-là. Un jour sans horaire = jour fermé.
      </p>

      <div className="dispo-layout">
        <div className="calendar-container">
          <div className="calendar-header">
            <button onClick={previousMonth} className="nav-button">&lt; Précédent</button>
            <h2>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
            <button onClick={nextMonth} className="nav-button">Suivant &gt;</button>
          </div>

          <div className="calendar-grid">
            <div className="weekday-header">Lun</div>
            <div className="weekday-header">Mar</div>
            <div className="weekday-header">Mer</div>
            <div className="weekday-header">Jeu</div>
            <div className="weekday-header">Ven</div>
            <div className="weekday-header">Sam</div>
            <div className="weekday-header">Dim</div>
            {renderCalendar()}
          </div>
        </div>

        {selectedDay && (
          <div className="day-editor">
            <div className="day-editor-header">
              <h3>{new Date(selectedDay + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
              <button className="close-btn" onClick={closeDay}>✕</button>
            </div>

            <div className="heures-list">
              {editingHeures.length === 0 && <p className="empty-hint">Aucun horaire — jour fermé.</p>}
              {editingHeures.map(h => (
                <div key={h.heure} className={`heure-chip ${!h.disponible ? 'reserved' : ''}`}>
                  <span>{h.heure}</span>
                  {h.disponible ? (
                    <button onClick={() => removeHeure(h.heure)} title="Retirer cet horaire">✕</button>
                  ) : (
                    <span className="reserved-label" title="Déjà réservé, non supprimable ici">réservé</span>
                  )}
                </div>
              ))}
            </div>

            <div className="add-heure-row">
              <input
                type="time"
                value={newHeure}
                onChange={e => setNewHeure(e.target.value)}
              />
              <button onClick={addHeure}>+ Ajouter</button>
            </div>

            <label className="duplicate-row">
              <input
                type="checkbox"
                checked={dupliquer}
                onChange={e => setDupliquer(e.target.checked)}
              />
              Dupliquer sur les
              <input
                type="number"
                min="1"
                max="12"
                value={nbSemaines}
                onChange={e => setNbSemaines(parseInt(e.target.value, 10) || 1)}
                className="weeks-input"
                disabled={!dupliquer}
              />
              semaines suivantes (même jour)
            </label>

            <button className="save-day-button" onClick={saveDay} disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer ce jour'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDisponibilitesPage;
